import { useMemo } from "react";
import { resolveDatePreset, type ResolvedDateRange } from "#app/lib/utils/date-presets";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { ActivityWhereInput } from "#app/lib/data";
import type { DashboardScope } from "./dashboard-scope-context";
import { useEffectiveScope, type ScopedWhereOptions } from "./scope-shared";
import { useDateBoundaryKey } from "#app/lib/hooks/use-date-boundary-key";
import { at } from "#app/lib/utils/at";

export type ScopedActivityWhere = {
  /** The merged `ActivityWhereInput`: the widget's own `base` AND the (effective) date
   *  window AND the scope facets the activity schema can express (team, actor,
   *  Only-Me) — ready to drop into an `activities` query. Always an object, stable
   *  across renders (recomputes only when the effective scope or base changes). */
  where: ActivityWhereInput;
  /** The resolved `{ startDate, endDate }` of the active window — for axis labels. */
  dateRange: ResolvedDateRange;
  /** The effective scope (default off-dashboard; any per-widget override folded in). */
  scope: DashboardScope;
};

/**
 * The activity-shaped twin of {@link useScopedWhere}. Activities are a DIFFERENT
 * query shape (`ActivityWhereInput`: `subject`/`creationDate`/`createdBy`/`teamIds`,
 * not the issue `where`), so an activity-backed widget cannot reuse the issue fold —
 * it reaches for THIS fold instead. Same ambient window, same `override` seam; only
 * the target `where` shape differs. A widget NEVER hand-builds an activity `where`.
 *
 * Every facet the issue fold expresses maps onto the activity shape here — resolved
 * ONCE, canonically, rather than silently dropped per widget:
 * - **Team entity scope** narrows by the activity's teams (`teamIds`). **Member
 *   entity scope** (My Work) narrows by the activity's **actor** (`createdBy`) — the
 *   closest analog to the issue fold's assignee narrowing (activities have no
 *   assignee), and the semantics the feed promises ("actions you've taken").
 * - **Workflow entity scope** cannot narrow activities (`ActivityWhereInput` exposes
 *   `teamIds`/`issueId`, not `workflowId`); a workflow-scoped widget folds
 *   `scope.entityWhere` through the query's separate `issueWhere` arg instead (the
 *   server resolves the parent issues — see `use-activity-group-counts.ts` and
 *   `use-activity-list.ts`). Never faked client-side.
 * - **Member set/Only-Me** (the toolbar window) narrow by the actor too (`createdBy`).
 *
 * @param base A fixed activity predicate the widget always wants (e.g. `{ subject:
 *   { eq: "stage" } }`). May be inline — folded in via serialization, never a memo dep.
 */
export function useScopedActivityWhere(
  base?: ActivityWhereInput | null,
  opts?: ScopedWhereOptions,
): ScopedActivityWhere {
  const { scope, effDateRange, effMemberIds, effOnlyMe } = useEffectiveScope(opts?.override);
  const { userId } = useWorkspaceData();
  const dateField = opts?.dateField === undefined ? "creationDate" : opts.dateField;
  // Re-resolve relative presets when the local day rolls over (B8); absolute
  // presets skip the signal so they don't churn variables at midnight.
  const dayKey = useDateBoundaryKey();
  const dateIsRelative = effDateRange.key !== "all-time" && effDateRange.key !== "custom";

  // Serialize exactly the effective inputs the merge reads, so an inline `base`/
  // `override` (a fresh ref each render) never churns the query variables. `entity`
  // rides as `type:id` — team and member entities reach the activity `where`.
  const baseKey = base ? JSON.stringify(base) : "";
  const scopeKey = JSON.stringify({
    en: scope.entity ? `${scope.entity.type}:${scope.entity.id}` : null,
    k: effDateRange.key,
    c: effDateRange.custom ?? null,
    m: effMemberIds,
    o: effOnlyMe ? (userId ?? null) : false,
    f: dateField,
    d: dateIsRelative ? dayKey : "",
  });

  return useMemo<ScopedActivityWhere>(() => {
    const dateRange = resolveDatePreset(effDateRange.key, effDateRange.custom);
    const and: ActivityWhereInput[] = [];
    if (base) and.push(base);
    // "all-time" means no date bound — drop the clause (see `useScopedWhere`).
    if (dateField && effDateRange.key !== "all-time") {
      and.push({ [dateField]: { gte: dateRange.startDate, lte: dateRange.endDate } });
    }
    // Entity scope with an activity analog folds here (see the doc note): a team
    // narrows by the activity's teams, a member (My Work) by its actor. Workflow
    // has no analog and compensates through `issueWhere` at the query instead.
    // teamIds emits the canonical `{ in: [id] }` form (the server handles eq + in
    // wherever teamIds appears; in is the shape every other id-filter uses).
    if (scope.entity?.type === "team") {
      and.push({ teamIds: { in: [scope.entity.id] } });
    } else if (scope.entity?.type === "member") {
      and.push({ createdBy: { eq: scope.entity.id } });
    }
    // Only-Me pins the actor to the current user; a member set narrows by actor too.
    // Only-Me wins (it's the more specific "just me") when both are set.
    if (effOnlyMe && userId) {
      and.push({ createdBy: { eq: userId } });
    } else if (effMemberIds.length > 0) {
      and.push({ createdBy: { in: effMemberIds } });
    }
    const where: ActivityWhereInput =
      and.length === 0 ? {} : and.length === 1 ? at(and, 0, "condition") : { AND: and };
    const effectiveScope: DashboardScope = {
      ...scope,
      dateRange: effDateRange,
      memberIds: effMemberIds,
      onlyMe: effOnlyMe,
    };
    return { where, dateRange, scope: effectiveScope };
    // baseKey/scopeKey capture every value the merge reads; see useScopedWhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseKey, scopeKey]);
}
